import {
  Add as AddIcon,
  AssignmentTurnedIn as TaskIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  Cake as CakeIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  DirectionsCar as DirectionsCarIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  FlashOn,
  Insights as InsightsIcon,
  LocalShipping as LocalShippingIcon,
  NoteAlt as NoteIcon,
  Person as PersonIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Phone as PhoneIcon,
  PersonAdd as PersonAddIcon,
  PostAdd as PostAddIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Sms as SmsIcon,
  Stars as StarsIcon,
  TrendingUp as TrendingUpIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import type {
  Contact,
  ContactActivity,
  ContactActivityType,
  ContactStage,
  CrmActivityDomain,
} from '@otw/models/leads/contacts';
import { formatContactBirthday } from '@otw/models/leads/contacts';
import { getContactStageAt } from '@otw/models/leads/contactStageHistory';
import type { Lead } from '@otw/models/leads/lead';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PrivateUserInfoType } from '@otw/models';
import { useLeads } from 'hooks/admin/useLeads';
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads';
import { useLeadColumns } from 'hooks/admin/useLeadColumns';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import { useContacts } from 'hooks/contacts/useContacts';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import type { ContactCompany, ContactCompanyRole } from '@otw/models/leads/contactCompanies';
import {
  CreateDisabledUser,
  type CreateDisabledUserInitialValues,
} from 'views/SuperAdmin/Management/UserManagement/components/CreateDisabledUser';
import { useTasks } from 'hooks/tasks/useTasks';
import { useClientActivity } from 'hooks/admin/useClientActivity';
import { usePickupLocations } from 'hooks/inventory/usePickupLocations';
import { useTerminals } from 'hooks/terminals/useTerminals';
import { getTerminalDisplayName } from '@otw/models/leads/terminals';
import { resolveTerminalLocation } from '@/core/terminals/terminalAddress';
import { getContactTerminals } from 'views/SuperAdmin/Management/TerminalManagement/util/terminalCompanyLinks';
import { openTerminalDetailInNewTab } from 'views/SuperAdmin/Management/TerminalManagement/util/terminalRoutes';
import { getUserDisplayName, useSalesAssignableUsers, useUsers } from 'hooks/useUsers';
import { usePermissions } from 'hooks/auth/usePermissions';
import {
  ActivityTimelineEntryDate,
  ActivityTimelineEntryTitle,
} from './ActivityTimelineEntry';
import { activityAuthorDisplayName } from '../util/activityDisplay';
import { contactToTaskCustomerOption } from '../util/contactLeadSync';
import { logTaskCreationContactActivity } from '../util/contactTaskActivity';
import { openCompanyDetailInNewTab } from '../util/companyRoutes';
import { resolveOwnerDisplayName as resolveOwnerDisplayNameUtil } from '../util/resolveOwnerDisplayName';
import { openContactDetailInNewTab } from '../util/contactRoutes';
import { formatPickupLocationAddress } from '../util/pickupLocationDisplay';
import { previewAccordionSummarySx } from '../util/previewAccordionSummarySx';
import { previewPaneBodySx, previewPanePaperSx } from '../util/previewPanePaperSx';
import {
  formatCurrency,
  formatNumber,
  getDealQualityLabel,
  getVehicleSoldValue,
  normalizeText,
} from '../util/customerUtils';
import {
  buildContactMergedTimelineEntries,
  contactMergedTimelineEntryAuthorId,
  filterContactMergedTimelineEntries,
  getContactMergedTimelineDisplay,
  getContactMergedTimelineDomain,
  type ContactMergedActivityFilter,
  type ContactMergedTimelineEntry,
  type CrmActivityDomainFilter,
} from '../util/contactMergedTimeline';
import { activityDomainPaperSx } from '../util/activityDomainPaperSx';
import { ActivityDomainLabel } from './ActivityDomainLabel';
import {
  contactMatchesLead,
  pickLeadForAboutStatus,
} from '../util/contactLeadSync';
import { transferContactDriverLicenseToUser } from '../util/contactDriverLicenseTransfer';
import { getContactPrimaryCompanyName } from '../util/contactCompanyMembership';
import {
  ContactActivityLogDialog,
  ContactCompanyFormDialog,
  InventoryLeadFormDialog,
  LeadFormDialog,
  SelectCompanyForLeadDialog,
  TaskFormDialog,
  type CompanyMemberForLead,
  type InitialClientForLead,
  type LoggableActivityType,
  type TaskAssignableUser,
  type TaskFormSubmitPayload,
} from './dialogs';
import type { TaskCustomerOption } from '../util/taskUtils';

const getLeadActivityNote = (lead: Lead): string => {
  const company = lead.company?.trim();
  const who = lead.customerName?.trim();
  if (company && who) return `${company} · ${who}`;
  return company || who || 'New lead';
};

const getInventoryLeadActivityNote = (
  lead: InventoryLead,
  companyName: string,
  contactName: string,
): string => {
  const truck = lead.trucks?.[0];
  const truckLabel = truck
    ? [truck.year, truck.make, truck.model].filter(Boolean).join(' ')
    : '';
  const who = [companyName?.trim(), contactName?.trim()].filter(Boolean).join(' · ');
  if (who && truckLabel) return `${who} · ${truckLabel}`;
  return who || truckLabel || 'New inventory lead';
};

const contactToCreateAccountInitialValues = (
  contact: Contact,
  contactCompanies: ContactCompany[],
): CreateDisabledUserInitialValues => {
  const displayName = contact.name.trim();
  const parts = displayName.split(/\s+/).filter(Boolean);
  const company_name = getContactPrimaryCompanyName(contact.id, contactCompanies);
  return {
    first_name: parts[0] ?? '',
    last_name: parts.slice(1).join(' '),
    ...(contact.email?.trim() ? { email: contact.email.trim() } : {}),
    ...(contact.phone?.trim() ? { phone: contact.phone.trim() } : {}),
    ...(company_name ? { company_name } : {}),
  };
};

/** Only when the contact is already linked to a portal user (`contact.userId`). */
const contactToInitialClientForLead = (
  contact: Contact,
  contactCompanies: ContactCompany[],
): InitialClientForLead | null => {
  if (!contact.userId?.trim()) return null;
  const displayName = contact.name.trim();
  const parts = displayName.split(/\s+/).filter(Boolean);
  const first_name = parts[0] ?? '';
  const last_name = parts.slice(1).join(' ');
  const company_name = getContactPrimaryCompanyName(contact.id, contactCompanies);

  return {
    id: contact.userId.trim(),
    first_name,
    last_name,
    email: contact.email ?? '',
    phone: contact.phone,
    ...(company_name ? { company_name } : {}),
    ...(displayName ? { displayName } : {}),
  };
};

const COMPANY_ROLE_LABEL: Record<ContactCompanyRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  partner: 'Partner',
};

const ROLE_OPTIONS: { value: ContactCompanyRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'partner', label: 'Partner' },
];

interface ContactPreviewPaneProps {
  contact: Contact;
  onClose: () => void;
  onEdit?: (contact: Contact) => void;
}

const STAGE_LABEL: Record<ContactStage, string> = {
  prospect: 'Prospect',
  opportunity: 'Opportunity',
  customer: 'Customer',
  unqualified: 'Unqualified',
};

const stageAccentColor = (stage: ContactStage, palette: Theme['palette']): string => {
  if (stage === 'customer') return palette.success.main;
  if (stage === 'opportunity') return palette.info.main;
  if (stage === 'unqualified') return palette.text.secondary;
  return palette.warning.main;
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return dateFormatter.format(d);
};

const toDateMs = (value: Date | string | null | undefined): number => {
  if (!value) return -Infinity;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? -Infinity : parsed.getTime();
};

interface AboutRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const AboutRow: React.FC<AboutRowProps> = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box sx={{ color: 'text.secondary', mt: 0.25, display: 'flex' }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>
        {typeof value === 'string' ? (
          <Typography variant="body2">{value || '—'}</Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  </Stack>
);


interface ActivityIconMeta {
  icon: React.ReactNode;
  color: (palette: Theme['palette']) => string;
}

const ACTIVITY_ICON_META: Record<ContactActivityType | 'created', ActivityIconMeta> = {
  created: {
    icon: <PersonAddAlt1Icon fontSize="small" />,
    color: (p) => p.primary.main,
  },
  call: {
    icon: <PhoneIcon fontSize="small" />,
    color: (p) => p.success.main,
  },
  sms: {
    icon: <SmsIcon fontSize="small" />,
    color: (p) => p.info.main,
  },
  email: {
    icon: <EmailIcon fontSize="small" />,
    color: (p) => p.primary.main,
  },
  note: {
    icon: <NoteIcon fontSize="small" />,
    color: (p) => p.warning.main,
  },
  task: {
    icon: <TaskIcon fontSize="small" />,
    color: (p) => p.secondary.main,
  },
  lead: {
    icon: <PostAddIcon fontSize="small" />,
    color: (p) => p.primary.dark,
  },
  shipping_repairs: {
    icon: <LocalShippingIcon fontSize="small" />,
    color: (p) => p.secondary.main,
  },
};

type ActivityFilter = ContactMergedActivityFilter;

const ACTIVITY_FILTER_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'All activity' },
  { value: 'created', label: 'Contact created' },
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Emails' },
  { value: 'note', label: 'Notes' },
  { value: 'task', label: 'Tasks' },
  { value: 'lead', label: 'Leads' },
  { value: 'shipping_repairs', label: 'Shipping & Repairs' },
];

const DOMAIN_FILTER_OPTIONS: { value: CrmActivityDomainFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sales', label: 'Sales' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'marketing', label: 'Marketing' },
];

interface InsightStatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  accent: string;
}

const InsightStatCard: React.FC<InsightStatCardProps> = ({ icon, label, value, accent }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 2,
      borderColor: alpha(accent, 0.25),
      background: `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, ${alpha(accent, 0.02)} 100%)`,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar
        variant="rounded"
        sx={{
          width: 30,
          height: 30,
          bgcolor: alpha(accent, 0.16),
          color: accent,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.1, fontSize: 11 }}
          noWrap
        >
          {label}
        </Typography>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ lineHeight: 1.2 }}
          noWrap
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const ContactPreviewPane: React.FC<ContactPreviewPaneProps> = ({
  contact,
  onClose,
  onEdit,
}) => {
  const theme = useTheme();
  const { showError } = useSnackbar();
  const { leads } = useLeads();
  const { inventoryLeads } = useInventoryLeads();
  const { getColumnLabel, getColumnColor } = useLeadColumns();
  const queryClient = useQueryClient();
  const { logContactActivity, linkContactPortalUser, isLoggingActivity, isLinkingPortalUser, contacts } =
    useContacts();
  const { companies: contactCompanies, logCompanyActivity, addCompanyMember, isMutatingMembers } =
    useContactCompanies();
  const { createTask } = useTasks();
  const { soldVehicles } = useClientActivity({ dateRange: 'all' });
  const { users } = useUsers();
  const { leadManagementScope } = usePermissions();
  const hasFullLeadManagementAccess = leadManagementScope === 'all';
  const { users: salesAssignableUsers } = useSalesAssignableUsers();
  const { pickupLocations } = usePickupLocations();
  const { terminals } = useTerminals();

  const resolveOwnerDisplayName = useCallback(
    (ownerId: Contact['ownerId']) => resolveOwnerDisplayNameUtil(ownerId, users),
    [users],
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [logType, setLogType] = useState<LoggableActivityType | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [leadFormMountKey, setLeadFormMountKey] = useState(0);
  const [pickCompanyOpen, setPickCompanyOpen] = useState(false);
  const [companyForNewLead, setCompanyForNewLead] = useState<ContactCompany | null>(null);
  const [inventoryPickCompanyOpen, setInventoryPickCompanyOpen] = useState(false);
  const [companyForNewInventoryLead, setCompanyForNewInventoryLead] =
    useState<ContactCompany | null>(null);
  const [inventoryLeadFormOpen, setInventoryLeadFormOpen] = useState(false);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [companyFormOpen, setCompanyFormOpen] = useState(false);
  const [addCompanyValue, setAddCompanyValue] = useState<ContactCompany | null>(null);
  const [addCompanyRole, setAddCompanyRole] = useState<ContactCompanyRole>('manager');

  const hasPortalUser = Boolean(contact.userId?.trim());
  const [activitySearch, setActivitySearch] = useState('');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [activityDomainFilter, setActivityDomainFilter] =
    useState<CrmActivityDomainFilter>('all');

  /** All leads linked via `leadIds` or the same email/phone/name key (matches contact sync). */
  const contactLeads = useMemo<Lead[]>(() => {
    if (!leads?.length) return [];
    const matched: Lead[] = [];
    const seen = new Set<string>();
    for (const lead of leads) {
      if (!contactMatchesLead(contact, lead)) continue;
      if (seen.has(lead.id)) continue;
      seen.add(lead.id);
      matched.push(lead);
    }
    return matched;
  }, [leads, contact]);

  /** Prefer active pipeline leads so returning customers show the open deal's status first. */
  const leadStatusForAbout = useMemo<Lead | null>(
    () => pickLeadForAboutStatus(contactLeads),
    [contactLeads],
  );

  const memberCompanies = useMemo(
    () =>
      contactCompanies.filter((company) =>
        company.members.some((m) => m.contactId === contact.id),
      ),
    [contactCompanies, contact.id],
  );

  const linkedCompanyRows = useMemo(
    () =>
      memberCompanies.map((company) => ({
        company,
        role:
          company.members.find((m) => m.contactId === contact.id)?.role ?? ('manager' as const),
      })),
    [memberCompanies, contact.id],
  );

  const contactTerminalLinks = useMemo(
    () => getContactTerminals(contact.id, contactCompanies, terminals),
    [contact.id, contactCompanies, terminals],
  );

  const primaryCompanyName = useMemo(
    () => getContactPrimaryCompanyName(contact.id, contactCompanies),
    [contact.id, contactCompanies],
  );

  const addableCompanies = useMemo(
    () =>
      contactCompanies.filter(
        (company) => !company.members.some((m) => m.contactId === contact.id),
      ),
    [contactCompanies, contact.id],
  );

  const timelineEntries = useMemo<ContactMergedTimelineEntry[]>(
    () =>
      buildContactMergedTimelineEntries({
        contact,
        memberCompanies,
        leads: leads ?? [],
        inventoryLeads: inventoryLeads ?? [],
      }),
    [contact, memberCompanies, leads, inventoryLeads],
  );

  const contactSoldVehicles = useMemo(() => {
    if (!soldVehicles || soldVehicles.length === 0) return [];
    const contactNameKey = normalizeText(contact.name);
    return soldVehicles.filter((vehicle) => {
      const soldTo = vehicle.sold_to_user;
      if (!soldTo) return false;
      if (contact.userId && soldTo.id && soldTo.id === contact.userId) return true;
      if (contactNameKey && normalizeText(soldTo.name) === contactNameKey) return true;
      return false;
    });
  }, [soldVehicles, contact.userId, contact.name]);

  const insights = useMemo(() => {
    const totalValue = contactSoldVehicles.reduce(
      (sum, vehicle) => sum + getVehicleSoldValue(vehicle),
      0,
    );
    const vehiclesSold = contactSoldVehicles.length;
    const goodDeals = contactSoldVehicles.filter(
      (v) => getDealQualityLabel(v.dealQuality) === 'Good',
    ).length;
    const goodRate = vehiclesSold > 0 ? Math.round((goodDeals / vehiclesSold) * 100) : 0;
    const avgValue = vehiclesSold > 0 ? totalValue / vehiclesSold : 0;
    const leadsCreated = Math.max(
      contact.leadIds?.length ?? 0,
      contactLeads.length,
    );
    return {
      vehiclesSold,
      leadsCreated,
      totalValue,
      goodRate,
      avgValue,
      companyCount: linkedCompanyRows.length,
    };
  }, [contactSoldVehicles, contactLeads.length, contact.leadIds, linkedCompanyRows.length]);

  const visibleTimelineEntries = useMemo<ContactMergedTimelineEntry[]>(
    () =>
      filterContactMergedTimelineEntries(
        timelineEntries,
        activitySearch,
        activityFilter,
        activityDomainFilter,
      ),
    [timelineEntries, activitySearch, activityFilter, activityDomainFilter],
  );

  const accent = stageAccentColor(contact.stage, theme.palette);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handlePickLog = (type: LoggableActivityType) => {
    closeMenu();
    setLogType(type);
  };

  const handlePickTask = () => {
    closeMenu();
    setTaskDialogOpen(true);
  };

  const handlePickCreateLead = () => {
    if (!hasPortalUser) return;
    closeMenu();
    setCompanyForNewLead(null);
    setPickCompanyOpen(true);
  };

  const handleCompanyForNewLeadConfirmed = (company: ContactCompany) => {
    setCompanyForNewLead(company);
    setPickCompanyOpen(false);
    setLeadFormMountKey((k) => k + 1);
    setLeadFormOpen(true);
  };

  const handlePickCreateInventoryLead = () => {
    closeMenu();
    setCompanyForNewInventoryLead(null);
    setInventoryPickCompanyOpen(true);
  };

  const handleInventoryCompanyConfirmed = (company: ContactCompany) => {
    setCompanyForNewInventoryLead(company);
    setInventoryPickCompanyOpen(false);
    setInventoryLeadFormOpen(true);
  };

  const handleInventoryLeadCreated = async (lead: InventoryLead) => {
    if (!companyForNewInventoryLead) return;
    await logCompanyActivity({
      companyId: companyForNewInventoryLead.id,
      type: 'lead',
      note: getInventoryLeadActivityNote(lead, companyForNewInventoryLead.name, contact.name),
      leadId: lead.id,
      contactId: contact.id,
      domain: 'inventory',
    });
    setCompanyForNewInventoryLead(null);
  };

  const handleAddCompany = async () => {
    if (!addCompanyValue) return;
    await addCompanyMember({
      companyId: addCompanyValue.id,
      contactId: contact.id,
      role: addCompanyRole,
    });
    setAddCompanyValue(null);
    setAddCompanyRole('manager');
  };

  const createAccountInitialValues = useMemo(
    () => contactToCreateAccountInitialValues(contact, contactCompanies),
    [contact, contactCompanies],
  );

  const handleClientAccountCreated = async (user: PrivateUserInfoType) => {
    const userId = user.id?.trim();
    if (!userId) {
      showError('Client account was created but could not be linked to this contact. Please try again or contact support.');
      return;
    }
    await linkContactPortalUser({ contactId: contact.id, userId });
    if (contact.driver_license_front_storage_path?.trim()) {
      try {
        await transferContactDriverLicenseToUser(contact.id, userId);
      } catch {
        showError("Client account was linked, but the driver's license photo could not be copied. You can upload it from the client profile.");
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['users'] });
    setCreateAccountOpen(false);
  };

  const handleLeadCreatedFromPreview = async (lead: Lead) => {
    if (!companyForNewLead) return;
    await logCompanyActivity({
      companyId: companyForNewLead.id,
      type: 'lead',
      note: getLeadActivityNote(lead),
      leadId: lead.id,
      contactId: contact.id,
      domain: 'sales',
    });
  };

  const handleLogSubmit = async (note: string, domain: CrmActivityDomain) => {
    if (!logType) return;
    await logContactActivity({ contactId: contact.id, type: logType, note, domain });
    setLogType(null);
  };

  const handleTaskSubmit = async (task: TaskFormSubmitPayload) => {
    const created = await createTask(task);
    await logTaskCreationContactActivity(task, created.id, logContactActivity);
    setTaskDialogOpen(false);
  };

  const taskCustomerOption: TaskCustomerOption = useMemo(
    () => contactToTaskCustomerOption(contact, contactLeads),
    [contact, contactLeads],
  );

  const assignableUsers = useMemo<TaskAssignableUser[]>(() => {
    if (!hasFullLeadManagementAccess) return [];
    return salesAssignableUsers.map((user) => ({
      id: String(user.id),
      displayName: getUserDisplayName(user),
      email: user.email || null,
    }));
  }, [hasFullLeadManagementAccess, salesAssignableUsers]);

  const initialClientForNewLead = useMemo(() => {
    const base = contactToInitialClientForLead(contact, contactCompanies);
    if (!base) return null;
    if (companyForNewLead) {
      return { ...base, company_name: companyForNewLead.name };
    }
    return base;
  }, [contact, companyForNewLead, contactCompanies]);

  /** Member contacts of the picked company, hydrated with roles for the lead form. */
  const companyMembersForLead = useMemo<CompanyMemberForLead[]>(() => {
    if (!companyForNewLead) return [];
    const byId = new Map(contacts.map((c) => [c.id, c]));
    return companyForNewLead.members.flatMap((member) => {
      const hydrated = byId.get(member.contactId);
      return hydrated ? [{ contact: hydrated, role: member.role }] : [];
    });
  }, [companyForNewLead, contacts]);

  return (
    <Paper variant="outlined" sx={previewPanePaperSx}>
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1.5 }}
        >
          <Chip
            size="small"
            label={STAGE_LABEL[contact.stage]}
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: alpha(accent, 0.18),
              color: accent,
              '& .MuiChip-label': { px: 1 },
            }}
          />
          <Stack direction="row" spacing={0.5}>
            {onEdit && (
              <Tooltip title="Edit contact">
                <IconButton
                  size="small"
                  onClick={() => onEdit(contact)}
                  aria-label="Edit contact"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                onClick={() => openContactDetailInNewTab(contact.id)}
                aria-label="Open contact in new tab"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose} aria-label="Close preview">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: alpha(accent, 0.22),
              color: accent,
              fontSize: 24,
              fontWeight: 800,
            }}
          >
            {contact.name?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0 }}
            >
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}
                noWrap
              >
                {contact.name || 'Unnamed contact'}
              </Typography>
              {!hasPortalUser && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setCreateAccountOpen(true)}
                  disabled={isLinkingPortalUser}
                  sx={{
                    flexShrink: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 0.5,
                    px: 1.25,
                    fontSize: 12,
                  }}
                >
                  Create account
                </Button>
              )}
            </Stack>
            {primaryCompanyName ? (
              <Typography variant="body2" color="text.secondary" noWrap>
                {primaryCompanyName}
              </Typography>
            ) : null}
            {contact.email ? (
              <Link
                href={`mailto:${contact.email}`}
                variant="body2"
                color="primary"
                underline="hover"
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {contact.email}
              </Link>
            ) : null}
          </Box>
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={previewPaneBodySx}>
        {/* About */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Typography fontWeight={700}>About</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <AboutRow
                icon={<EmailIcon fontSize="small" />}
                label="Email"
                value={contact.email || '—'}
              />
              <AboutRow
                icon={<PhoneIcon fontSize="small" />}
                label="Phone"
                value={contact.phone || '—'}
              />
              <AboutRow
                icon={<CakeIcon fontSize="small" />}
                label="Birthday"
                value={formatContactBirthday(contact.birthday) || '—'}
              />
              <AboutRow
                icon={<PersonIcon fontSize="small" />}
                label="Sales owner"
                value={resolveOwnerDisplayName(contact.ownerId)}
              />
              <AboutRow
                icon={<PersonIcon fontSize="small" />}
                label="Inventory owner"
                value={resolveOwnerDisplayName(contact.inventoryOwnerId ?? null)}
              />
              <AboutRow
                icon={<ScheduleIcon fontSize="small" />}
                label="Last contacted"
                value={formatDate(contact.lastActivityAt)}
              />
              <AboutRow
                icon={<FlagIcon fontSize="small" />}
                label="Stage"
                value={
                  <Chip
                    size="small"
                    label={STAGE_LABEL[contact.stage]}
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: alpha(accent, 0.14),
                      color: accent,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                }
              />
              <AboutRow
                icon={<FlashOn fontSize="small" />}
                label="Most recent lead status"
                value={
                  leadStatusForAbout && leadStatusForAbout.status ? (
                    <Stack spacing={0.25}>
                      <Chip
                        size="small"
                        label={getColumnLabel(leadStatusForAbout.status)}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: alpha(
                            getColumnColor(leadStatusForAbout.status),
                            0.16,
                          ),
                          color: getColumnColor(leadStatusForAbout.status),
                          alignSelf: 'flex-start',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Stack>
                  ) : (
                    '—'
                  )
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Companies */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700}>Companies</Typography>
                <Chip
                  size="small"
                  label={linkedCompanyRows.length}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.secondary.main, 0.12),
                    color: theme.palette.secondary.dark,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setCompanyFormOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mr: 0.5 }}
              >
                Create company
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction="row"
              spacing={1}
              alignItems="stretch"
              sx={{ mb: 1.5 }}
            >
              <Autocomplete
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': { height: '100%', borderRadius: 2 },
                }}
                options={addableCompanies}
                value={addCompanyValue}
                onChange={(_, value) => setAddCompanyValue(value)}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {option.name || 'Unnamed company'}
                      </Typography>
                      {option.website ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.website}
                        </Typography>
                      ) : null}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search companies"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      sx: { borderRadius: 2, bgcolor: 'background.paper' },
                    }}
                  />
                )}
              />
              <TextField
                select
                size="small"
                value={addCompanyRole}
                onChange={(e) => setAddCompanyRole(e.target.value as ContactCompanyRole)}
                sx={{
                  flexShrink: 0,
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                  '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
                }}
                InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => void handleAddCompany()}
                disabled={!addCompanyValue || isMutatingMembers}
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  fontSize: 14,
                  lineHeight: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add
              </Button>
            </Stack>
            {linkedCompanyRows.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No companies linked yet. Add one above.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {linkedCompanyRows.map(({ company, role }) => (
                  <Paper
                    key={company.id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                    }}
                    onClick={() => openCompanyDetailInNewTab(company.id)}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BusinessIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {company.name || 'Unnamed company'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {COMPANY_ROLE_LABEL[role]}
                        </Typography>
                      </Box>
                      <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Terminals (read-only; derived via companies) */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={700}>Terminals</Typography>
              <Chip
                size="small"
                label={contactTerminalLinks.length}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  color: theme.palette.info.dark,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {contactTerminalLinks.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No terminals — terminals come from linked companies.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {contactTerminalLinks.map(({ terminal, companies: viaCompanies }) => {
                  const location = resolveTerminalLocation(terminal, pickupLocations);
                  const viaLabel =
                    viaCompanies.length === 1
                      ? `via ${viaCompanies[0].name || 'Unnamed company'}`
                      : `via ${viaCompanies.map((c) => c.name || 'Unnamed company').join(', ')}`;
                  return (
                    <Paper
                      key={terminal.id}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                      }}
                      onClick={() => openTerminalDetailInNewTab(terminal.id)}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WarehouseIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {getTerminalDisplayName(terminal, location)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {formatPickupLocationAddress(location) || 'No address'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {viaLabel}
                          </Typography>
                        </Box>
                        <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Recent activities */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Typography fontWeight={700}>Recent activities</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction="row"
              spacing={1}
              alignItems="stretch"
              sx={{ mb: 1.5 }}
            >
              <TextField
                size="small"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                placeholder="Search"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, bgcolor: 'background.paper' },
                }}
              />
              <TextField
                select
                size="small"
                value={activityDomainFilter}
                onChange={(e) =>
                  setActivityDomainFilter(e.target.value as CrmActivityDomainFilter)
                }
                sx={{
                  flexShrink: 0,
                  minWidth: 100,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                  '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
                }}
                InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
              >
                {DOMAIN_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value as ActivityFilter)}
                sx={{
                  flexShrink: 0,
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                  '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
                }}
                InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
              >
                {ACTIVITY_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={openMenu}
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  fontSize: 14,
                  lineHeight: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add
              </Button>
            </Stack>

            <Box
              sx={{
                maxHeight: '50vh',
                overflowY: 'auto',
                pr: 0.25,
                scrollbarGutter: 'stable',
              }}
            >
              {visibleTimelineEntries.length === 0 ? (
                <Box
                  sx={{
                    py: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                    border: 1,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">No activities match your filters.</Typography>
                </Box>
              ) : (
                <Timeline
                  sx={{
                    m: 0,
                    p: 0,
                    '& .MuiTimelineItem-root:before': { display: 'none', flex: 0, padding: 0 },
                  }}
                >
                  {visibleTimelineEntries.map((entry, index) => {
                    const isLast = index === visibleTimelineEntries.length - 1;
                    const display = getContactMergedTimelineDisplay(entry);
                    const meta = ACTIVITY_ICON_META[display.activityType];
                    const iconColor = meta.color(theme.palette);
                    const authorName = activityAuthorDisplayName(
                      users,
                      contactMergedTimelineEntryAuthorId(entry),
                    );
                    const entryStage = getContactStageAt(
                      contact.stageHistory,
                      entry.createdAt,
                      contact.stage,
                    );
                    const entryStageAccent = stageAccentColor(
                      entryStage,
                      theme.palette,
                    );
                    const domain = getContactMergedTimelineDomain(entry);
                    return (
                      <TimelineItem key={entry.id}>
                        <TimelineSeparator>
                          <TimelineDot
                            sx={{
                              bgcolor: alpha(iconColor, 0.16),
                              color: iconColor,
                              boxShadow: 'none',
                              m: 0,
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {meta.icon}
                          </TimelineDot>
                          {!isLast && (
                            <TimelineConnector sx={{ bgcolor: 'divider' }} />
                          )}
                        </TimelineSeparator>
                        <TimelineContent sx={{ pb: 1.5, pr: 0 }}>
                          <Paper
                            variant="outlined"
                            sx={activityDomainPaperSx(domain)}
                            title={
                              domain === 'inventory'
                                ? 'Inventory activity'
                                : domain === 'sales'
                                  ? 'Sales activity'
                                  : undefined
                            }
                          >
                            <Box sx={{ p: 1.5 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ rowGap: 0.5 }}
                              >
                                <ActivityTimelineEntryTitle
                                  title={display.title}
                                  authorName={authorName}
                                  variant="body2"
                                />
                                {display.companyName ? (
                                  <Chip
                                    size="small"
                                    icon={
                                      <BusinessIcon sx={{ fontSize: 14 }} />
                                    }
                                    label={display.companyName}
                                    sx={{
                                      height: 18,
                                      fontSize: 10.5,
                                      fontWeight: 600,
                                      bgcolor: alpha(
                                        theme.palette.secondary.main,
                                        0.12,
                                      ),
                                      color: theme.palette.secondary.dark,
                                      '& .MuiChip-icon': {
                                        color: 'inherit',
                                        ml: 0.5,
                                      },
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                ) : null}
                                <Chip
                                  size="small"
                                  label={STAGE_LABEL[entryStage]}
                                  sx={{
                                    height: 18,
                                    fontSize: 10.5,
                                    fontWeight: 600,
                                    bgcolor: alpha(entryStageAccent, 0.14),
                                    color: entryStageAccent,
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              </Stack>
                              <ActivityTimelineEntryDate
                                dateLabel={formatDate(entry.createdAt)}
                              />
                            </Box>
                            {display.body && (
                              <>
                                <Box sx={{ px: 1.5 }}>
                                  <Divider />
                                </Box>
                                <Box sx={{ p: 1.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                  >
                                    {display.body}
                                  </Typography>
                                </Box>
                              </>
                            )}
                            <ActivityDomainLabel domain={domain} />
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Customer insights */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={700}>Customer insights</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 1,
                mb: 1.5,
              }}
            >
              <InsightStatCard
                icon={<DirectionsCarIcon fontSize="small" />}
                label="Vehicles sold"
                value={formatNumber(insights.vehiclesSold)}
                accent={theme.palette.info.main}
              />
              <InsightStatCard
                icon={<StarsIcon fontSize="small" />}
                label="Leads created"
                value={formatNumber(insights.leadsCreated)}
                accent={theme.palette.warning.main}
              />
              <InsightStatCard
                icon={<AttachMoneyIcon fontSize="small" />}
                label="Total value"
                value={formatCurrency(insights.totalValue)}
                accent={theme.palette.success.main}
              />
              <InsightStatCard
                icon={<TrendingUpIcon fontSize="small" />}
                label="Good deal rate"
                value={`${insights.goodRate}%`}
                accent={theme.palette.success.dark}
              />
              <InsightStatCard
                icon={<AttachMoneyIcon fontSize="small" />}
                label="Avg sale"
                value={formatCurrency(insights.avgValue)}
                accent={theme.palette.primary.main}
              />
              <InsightStatCard
                icon={<BusinessIcon fontSize="small" />}
                label="Companies"
                value={formatNumber(insights.companyCount)}
                accent={theme.palette.secondary.main}
              />
            </Box>

          </AccordionDetails>
        </Accordion>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              mt: 0.75,
              minWidth: 240,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            },
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            Add activity
          </Typography>
        </Box>
        {(
          [
            { type: 'call', label: 'Log a call', subtitle: 'Phone call summary', icon: <PhoneIcon fontSize="small" />, accent: theme.palette.success.main },
            { type: 'sms', label: 'Log SMS', subtitle: 'Text message exchange', icon: <SmsIcon fontSize="small" />, accent: theme.palette.info.main },
            { type: 'email', label: 'Log an email', subtitle: 'Email correspondence', icon: <EmailIcon fontSize="small" />, accent: theme.palette.primary.main },
            { type: 'note', label: 'Create a note', subtitle: 'Internal note', icon: <NoteIcon fontSize="small" />, accent: theme.palette.warning.main },
            { type: 'shipping_repairs', label: 'Shipping & Repairs', subtitle: 'Shipping or repair update', icon: <LocalShippingIcon fontSize="small" />, accent: theme.palette.secondary.main },
          ] as const
        ).map((item) => (
          <MenuItem
            key={item.type}
            onClick={() => handlePickLog(item.type as LoggableActivityType)}
            sx={{
              py: 1,
              px: 1.75,
              '&:hover': { bgcolor: alpha(item.accent, 0.08) },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
                color: item.accent,
                '& .MuiSvgIcon-root': { color: item.accent },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: alpha(item.accent, 0.14),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.subtitle}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
        <Divider sx={{ my: 0 }} />
        <Tooltip
          title={
            hasPortalUser ? '' : 'Contact must have a client account'
          }
          placement="left"
        >
          <span>
            <MenuItem
              disabled={!hasPortalUser}
              onClick={handlePickCreateLead}
              sx={{
                py: 1,
                px: 1.75,
                width: '100%',
                '&:hover': hasPortalUser
                  ? { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                  : undefined,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: hasPortalUser
                    ? theme.palette.primary.dark
                    : theme.palette.action.disabled,
                  '& .MuiSvgIcon-root': {
                    color: hasPortalUser
                      ? theme.palette.primary.dark
                      : theme.palette.action.disabled,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, hasPortalUser ? 0.14 : 0.06),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PostAddIcon fontSize="small" />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Sales lead"
                secondary={
                  hasPortalUser
                    ? 'New opportunity for this contact'
                    : 'Contact must have a client account'
                }
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 12 }}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem
          onClick={handlePickCreateInventoryLead}
          sx={{
            py: 1,
            px: 1.75,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: theme.palette.primary.dark,
              '& .MuiSvgIcon-root': { color: theme.palette.primary.dark },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DirectionsCarIcon fontSize="small" />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Inventory lead"
            secondary="Track a truck this contact wants to sell"
            primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
        <Divider sx={{ my: 0 }} />
        <MenuItem
          onClick={handlePickTask}
          sx={{
            py: 1,
            px: 1.75,
            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: theme.palette.secondary.main,
              '& .MuiSvgIcon-root': { color: theme.palette.secondary.main },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.secondary.main, 0.14),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TaskIcon fontSize="small" />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Create a task"
            secondary="Schedule a follow-up"
            primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
      </Menu>

      <ContactActivityLogDialog
        open={Boolean(logType)}
        type={logType}
        contactName={contact.name}
        isSubmitting={isLoggingActivity}
        onClose={() => setLogType(null)}
        onSubmit={handleLogSubmit}
      />

      <TaskFormDialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        onSubmit={handleTaskSubmit}
        customers={[taskCustomerOption]}
        defaultCustomerId={contact.id}
        assignableUsers={assignableUsers.length > 0 ? assignableUsers : undefined}
      />

      <SelectCompanyForLeadDialog
        open={pickCompanyOpen}
        companies={contactCompanies}
        contactId={contact.id}
        contactName={contact.name}
        onClose={() => setPickCompanyOpen(false)}
        onConfirm={handleCompanyForNewLeadConfirmed}
      />

      <SelectCompanyForLeadDialog
        open={inventoryPickCompanyOpen}
        companies={contactCompanies}
        contactId={contact.id}
        contactName={contact.name}
        title="Which company is this inventory lead for?"
        onClose={() => setInventoryPickCompanyOpen(false)}
        onConfirm={handleInventoryCompanyConfirmed}
      />

      <InventoryLeadFormDialog
        open={inventoryLeadFormOpen}
        onClose={() => {
          setInventoryLeadFormOpen(false);
          setCompanyForNewInventoryLead(null);
        }}
        initialContactId={contact.id}
        initialCompanyId={companyForNewInventoryLead?.id ?? null}
        onLeadCreated={handleInventoryLeadCreated}
      />

      <LeadFormDialog
        key={leadFormMountKey}
        open={leadFormOpen}
        onClose={() => {
          setLeadFormOpen(false);
          setCompanyForNewLead(null);
        }}
        selectedLead={null}
        isCreating
        initialClient={initialClientForNewLead}
        selectedCompany={companyForNewLead}
        companyMembers={companyMembersForLead}
        defaultBusinessContactId={contact.id}
        initialContactId={contact.id}
        onLeadCreated={handleLeadCreatedFromPreview}
      />

      <ContactCompanyFormDialog
        open={companyFormOpen}
        onClose={() => setCompanyFormOpen(false)}
        defaultMemberContact={contact}
        defaultMemberRole="owner"
      />

      <CreateDisabledUser
        hideTrigger
        open={createAccountOpen}
        onOpenChange={setCreateAccountOpen}
        initialValues={createAccountInitialValues}
        onSuccess={handleClientAccountCreated}
      />
    </Paper>
  );
};

export default ContactPreviewPane;
